import { useState, useCallback, useMemo } from 'react';
import DEMO_TASKS from '../data/demoData';
import { formatDateTime, generateTaskId, autoDetectRisk } from '../utils/helpers';

export function useTaskStore(currentUser, userRole) {
  const [tasks, setTasks] = useState(DEMO_TASKS);
  const [logs, setLogs] = useState(() => {
    const allLogs = [];
    DEMO_TASKS.forEach(t => {
      (t.logs || []).forEach(l => {
        allLogs.push({ ...l, taskId: t.id, taskTitle: t.title });
      });
    });
    return allLogs.sort((a, b) => new Date(b.time) - new Date(a.time));
  });

  const addGlobalLog = useCallback((entry) => {
    setLogs(prev => [{ ...entry, time: entry.time || formatDateTime(new Date()) }, ...prev]);
  }, []);

  const updateTask = useCallback((updated) => {
    setTasks(prev => prev.map(t => (t.id === updated.id ? updated : t)));
  }, []);

  const addTask = useCallback((newTask) => {
    setTasks(prev => [newTask, ...prev]);
    addGlobalLog({
      content: `创建任务 ${newTask.id}`,
      type: 'create',
      user: currentUser,
      taskId: newTask.id,
      taskTitle: newTask.title,
    });
  }, [currentUser, addGlobalLog]);

  const deleteTask = useCallback((id) => {
    const t = tasks.find(x => x.id === id);
    setTasks(prev => prev.filter(x => x.id !== id));
    if (t) {
      addGlobalLog({
        content: `删除任务 ${t.id}`,
        type: 'delete',
        user: currentUser,
        taskId: t.id,
        taskTitle: t.title,
      });
    }
  }, [tasks, currentUser, addGlobalLog]);

  const batchUpdateStatus = useCallback((ids, newStatus) => {
    setTasks(prev => prev.map(t => {
      if (!ids.includes(t.id)) return t;
      const updated = {
        ...t,
        status: newStatus,
        logs: [...t.logs, { time: formatDateTime(new Date()), content: `批量状态变更为：${newStatus}`, type: 'batch', user: currentUser }],
      };
      addGlobalLog({
        content: `批量修改任务 ${t.id} 状态为 ${newStatus}`,
        type: 'batch',
        user: currentUser,
        taskId: t.id,
        taskTitle: t.title,
      });
      return updated;
    }));
  }, [currentUser, addGlobalLog]);

  const batchAssign = useCallback((ids, assignee) => {
    setTasks(prev => prev.map(t => {
      if (!ids.includes(t.id)) return t;
      const updated = {
        ...t,
        assignee,
        logs: [...t.logs, { time: formatDateTime(new Date()), content: `批量分配负责人：${assignee}`, type: 'batch', user: currentUser }],
      };
      addGlobalLog({
        content: `批量分配任务 ${t.id} 负责人为 ${assignee}`,
        type: 'batch',
        user: currentUser,
        taskId: t.id,
        taskTitle: t.title,
      });
      return updated;
    }));
  }, [currentUser, addGlobalLog]);

  const batchArchive = useCallback((ids) => {
    setTasks(prev => prev.map(t => {
      if (!ids.includes(t.id)) return t;
      const updated = {
        ...t,
        status: 'archived',
        archived: true,
        logs: [...t.logs, { time: formatDateTime(new Date()), content: '批量归档任务', type: 'batch', user: currentUser }],
      };
      addGlobalLog({
        content: `批量归档任务 ${t.id}`,
        type: 'batch',
        user: currentUser,
        taskId: t.id,
        taskTitle: t.title,
      });
      return updated;
    }));
  }, [currentUser, addGlobalLog]);

  const addComment = useCallback((taskId, content, replyTo = null) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const newComment = { user: currentUser, time: formatDateTime(new Date()), content, replies: [] };
      const updated = { ...t };
      if (replyTo != null) {
        updated.comments = t.comments.map((c, idx) => {
          if (idx !== replyTo) return c;
          return { ...c, replies: [...c.replies, { user: currentUser, time: formatDateTime(new Date()), content }] };
        });
      } else {
        updated.comments = [...t.comments, newComment];
      }
      updated.logs = [...t.logs, { time: formatDateTime(new Date()), content: replyTo != null ? '回复评论' : '添加评论', type: 'comment', user: currentUser }];
      return updated;
    }));
    addGlobalLog({ content: `在任务 ${taskId} 添加评论`, type: 'comment', user: currentUser, taskId, taskTitle: tasks.find(x => x.id === taskId)?.title });
  }, [currentUser, addGlobalLog, tasks]);

  const addApproval = useCallback((taskId, decision, comment) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const round = (t.approvals || []).length + 1;
      const newApproval = { user: currentUser, time: formatDateTime(new Date()), decision, comment, round };
      const newStatus = decision === 'approve' ? 'completed' : 'rejected';
      const updated = {
        ...t,
        status: newStatus,
        approvals: [...(t.approvals || []), newApproval],
        logs: [...t.logs, { time: formatDateTime(new Date()), content: `${currentUser} ${decision === 'approve' ? '审批通过' : '驳回'}：${comment}`, type: 'approve', user: currentUser }],
      };
      return updated;
    }));
    addGlobalLog({ content: `审批任务 ${taskId}：${decision === 'approve' ? '通过' : '驳回'}`, type: 'approve', user: currentUser, taskId, taskTitle: tasks.find(x => x.id === taskId)?.title });
  }, [currentUser, addGlobalLog, tasks]);

  const changeStatus = useCallback((taskId, newStatus) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      return {
        ...t,
        status: newStatus,
        logs: [...t.logs, { time: formatDateTime(new Date()), content: `状态变更为：${newStatus}`, type: 'status', user: currentUser }],
      };
    }));
    addGlobalLog({ content: `任务 ${taskId} 状态变更为 ${newStatus}`, type: 'status', user: currentUser, taskId, taskTitle: tasks.find(x => x.id === taskId)?.title });
  }, [currentUser, addGlobalLog, tasks]);

  const createTask = useCallback((form) => {
    const id = generateTaskId();
    const riskLevel = autoDetectRisk({ priority: form.priority, type: form.type });
    const newTask = {
      id,
      ...form,
      status: 'pending',
      createdAt: new Date().toISOString(),
      aiOutput: '',
      actions: [],
      approvals: [],
      logs: [{ time: formatDateTime(new Date()), content: '任务创建', type: 'create', user: currentUser }],
      reviewResult: '',
      comments: [],
      tags: form.tags || [],
      riskLevel,
      archived: false,
      llmConfig: null,
    };
    addTask(newTask);
    return newTask;
  }, [currentUser, addTask]);

  const updateTaskField = useCallback((taskId, patch) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const updated = { ...t, ...patch };
      updated.logs = [...t.logs, { time: formatDateTime(new Date()), content: '编辑任务信息', type: 'edit', user: currentUser }];
      return updated;
    }));
    addGlobalLog({ content: `编辑任务 ${taskId}`, type: 'edit', user: currentUser, taskId, taskTitle: tasks.find(x => x.id === taskId)?.title });
  }, [currentUser, addGlobalLog, tasks]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const processing = tasks.filter(t => t.status === 'processing').length;
    const review = tasks.filter(t => t.status === 'review').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const rejected = tasks.filter(t => t.status === 'rejected').length;
    const archived = tasks.filter(t => t.status === 'archived').length;
    const overdue = tasks.filter(t => t.status === 'overdue').length;
    const mine = tasks.filter(t => t.assignee === currentUser || t.collaborators.includes(currentUser)).length;
    const highRisk = tasks.filter(t => t.riskLevel === 'high' && !['completed', 'archived'].includes(t.status)).length;
    return { total, pending, processing, review, completed, rejected, archived, overdue, mine, highRisk };
  }, [tasks, currentUser]);

  return {
    tasks, setTasks, logs, stats,
    updateTask, addTask, deleteTask,
    batchUpdateStatus, batchAssign, batchArchive,
    addComment, addApproval, changeStatus,
    createTask, updateTaskField,
  };
}
